// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Function to decode JWT token
const decodeJWT = (token) => {
  try {
    if (!token) return null;
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const accessToken = localStorage.getItem("access_token");

    if (accessToken) {
      // Try to get user data from multiple sources
      const savedUser = localStorage.getItem("geeth365_user");
      const userFirstName = localStorage.getItem("user_first_name");
      const userId = localStorage.getItem("user_id");

      if (savedUser) {
        // Use the saved user data
        setUser(JSON.parse(savedUser));
      } else if (userFirstName && userId) {
        // Create user data from localStorage items
        const decodedToken = decodeJWT(accessToken);
        const userData = {
          id: userId,
          first_name: userFirstName,
          phone_number: decodedToken?.phone_number || "",
          roles: decodedToken?.roles || [],
          email:
            decodedToken?.email || `${decodedToken?.phone_number}@geeth365.com`,
        };
        setUser(userData);
        localStorage.setItem("geeth365_user", JSON.stringify(userData));
      } else {
        // Decode from token as fallback
        const decodedToken = decodeJWT(accessToken);
        if (decodedToken) {
          const userData = {
            id: decodedToken.sub,
            first_name: decodedToken.first_name,
            phone_number: decodedToken.phone_number,
            roles: decodedToken.roles || [],
            email:
              decodedToken.email || `${decodedToken.phone_number}@geeth365.com`,
          };
          setUser(userData);
          localStorage.setItem("geeth365_user", JSON.stringify(userData));
        }
      }
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem("geeth365_user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    // Clear all user-related data from localStorage
    localStorage.removeItem("access_token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("user_first_name");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_roles");
    localStorage.removeItem("geeth365_user");
  };

  const signup = (userData) => {
    const newUser = {
      ...userData,
      id: Date.now().toString(),
      subscriptionActive: true,
      createdAt: new Date().toISOString(),
    };
    setUser(newUser);
    localStorage.setItem("geeth365_user", JSON.stringify(newUser));
    return newUser;
  };

  const isAuthenticated = !!user && !!localStorage.getItem("access_token");

  const value = {
    user,
    login,
    logout,
    signup,
    isAuthenticated,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
